import { Target, Layers, Scale, GitMerge, BarChart3, AlertTriangle } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Integrated SEO + SEM strategy page.
 * Distinct from /servicios/google-ads (channel execution) and
 * /servicios/seo-tecnico (technical SEO execution): this is the
 * allocation and sequencing layer that decides how the two channels work together.
 */
export const seoSemStrategiesPage: MoneyPageContent = {
  id: "seo-sem-strategies",
  icon: Scale,
  serviceType: "SEO and SEM Strategy",
  esPath: "/servicios/seo-sem-estrategias",
  enPath: "/en/services/seo-sem-strategies",
  es: {
    metaTitle: "Estrategia SEO y SEM | Asignación de canales de búsqueda",
    metaDescription:
      "Estrategia integrada de SEO y SEM: qué palabras clave priorizar en cada canal, cómo secuenciar la inversión y cómo medir sin duplicar gasto.",
    keywords: [
      "estrategia seo y sem",
      "seo y sem integrado",
      "asignación de presupuesto de búsqueda",
      "solapamiento de palabras clave",
      "medición seo sem",
      "estrategia de búsqueda",
    ],
    h1: "Estrategia integrada de SEO y SEM para decidir dónde invierte cada canal",
    breadcrumbLabel: "SEO y SEM",
    heroSubtitle: "Una sola estrategia de búsqueda, dos canales",
    heroDescription:
      "SEO y SEM suelen gestionarse por separado, con equipos o proveedores distintos que no se hablan. El resultado es presupuesto pagado compitiendo con posiciones orgánicas que ya funcionan, o al revés: dependencia de un canal lento cuando el negocio necesita resultados ahora. Trabajo la asignación de canal, la secuencia de inversión y la medición compartida entre ambos.",
    ctaPrimary: {
      label: "Solicitar diagnóstico estratégico",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos", to: "/casos-de-exito" },
    summary: [
      {
        label: "Asignación de canal",
        text: "Qué palabras clave e intenciones conviene perseguir de forma orgánica, cuáles con pago y cuáles con ambos.",
      },
      {
        label: "Secuenciación",
        text: "En qué orden invertir cuando el tiempo, el presupuesto y la competencia no permiten hacerlo todo a la vez.",
      },
      {
        label: "Medición compartida",
        text: "Un marco de medición que evite atribuir el mismo resultado dos veces a canales distintos.",
      },
      {
        label: "Eliminación de gasto duplicado",
        text: "Detectar dónde se está pagando por algo que el SEO ya está entregando de forma gratuita.",
      },
    ],
    explanation: {
      title: "Qué es una estrategia de SEO y SEM integrada (y qué no es)",
      definition:
        "Es la capa de decisión que está por encima de la ejecución de cada canal: define qué papel juega el SEO, qué papel juega el SEM, y cómo se complementan según el objetivo de negocio, el tiempo disponible y el presupuesto. No es gestionar ambos canales en paralelo sin coordinación, ni es una auditoría técnica de uno de los dos.",
      whyImportant: {
        title: "Por qué la asignación importa más que la ejecución aislada",
        text: "Optimizar SEO y SEM por separado, sin una decisión conjunta de dónde debe estar cada palabra clave, lleva a competir contigo mismo: pagar por clics en términos donde ya posicionas de forma orgánica, o esperar meses de trabajo orgánico cuando el negocio necesitaba visibilidad inmediata.",
      },
      consequences: {
        title: "Qué pasa sin una estrategia conjunta",
        text: "Presupuesto pagado gastado en palabras clave donde el sitio ya rankea, oportunidades orgánicas de largo plazo ignoradas por enfocarse solo en resultados inmediatos de Ads, y reportes de cada canal que no permiten ver el panorama completo de la inversión en búsqueda.",
      },
    },
    problems: {
      title: "Errores frecuentes al gestionar SEO y SEM por separado",
      subtitle: "La mayoría surgen de la falta de una decisión conjunta, no de mala ejecución individual",
      items: [
        {
          icon: GitMerge,
          title: "Solapamiento de palabras clave sin decisión",
          description:
            "Pagar por anuncios en términos donde el sitio ya tiene una posición orgánica sólida, sin haber evaluado si eso aporta incrementalidad.",
          impact: "Presupuesto pagado ocupando un espacio que el SEO ya cubría de forma gratuita.",
        },
        {
          icon: Layers,
          title: "Equipos o proveedores que no se comunican",
          description:
            "SEO y SEM gestionados por personas o agencias distintas, cada una optimizando su propio canal sin visibilidad del otro.",
          impact: "Decisiones contradictorias sobre qué páginas o términos priorizar.",
        },
        {
          icon: AlertTriangle,
          title: "Secuenciación equivocada de la inversión",
          description:
            "Invertir todo el presupuesto en SEM sin construir base orgánica, o esperar resultados orgánicos cuando el negocio necesitaba visibilidad en semanas, no meses.",
          impact: "Presupuesto mal distribuido frente al horizonte de tiempo real del negocio.",
        },
        {
          icon: BarChart3,
          title: "Medición fragmentada por canal",
          description:
            "Cada canal reportando sus propios números sin un marco común que muestre el efecto conjunto en el negocio.",
          impact: "Decisiones de presupuesto tomadas con una vista parcial del resultado real.",
        },
        {
          icon: Target,
          title: "Falta de criterio para decidir dónde competir",
          description:
            "Perseguir las mismas palabras clave de alto volumen en ambos canales sin considerar dificultad, margen o intención real de búsqueda.",
          impact: "Esfuerzo concentrado en términos que no son los que más convierten para el negocio.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo la estrategia de SEO y SEM",
      subtitle: "Decisión de canal antes que ejecución táctica",
      steps: [
        {
          number: "01",
          title: "Mapeo de intención y solapamiento",
          description:
            "Identificar qué palabras clave e intenciones ya están cubiertas orgánicamente, cuáles requieren pago y dónde ambos canales compiten entre sí.",
        },
        {
          number: "02",
          title: "Asignación de canal por objetivo",
          description:
            "Definir qué debe resolverse con SEO por su naturaleza de largo plazo y qué debe resolverse con SEM por su capacidad de resultado inmediato.",
        },
        {
          number: "03",
          title: "Secuenciación de la inversión",
          description:
            "Establecer en qué orden y con qué peso relativo invertir en cada canal según el horizonte de tiempo y el presupuesto disponible.",
        },
        {
          number: "04",
          title: "Marco de medición compartido",
          description:
            "Definir cómo se reporta el efecto conjunto de ambos canales para evitar atribuciones duplicadas o contradictorias.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Mapeo de solapamiento e intención entre SEO y SEM",
          "Definición de qué canal debe priorizarse por palabra clave o segmento",
          "Plan de secuenciación de inversión entre ambos canales",
          "Marco de medición compartido entre SEO y SEM",
          "Recomendaciones para coordinar equipos o proveedores existentes",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Ejecución operativa diaria de campañas de Google Ads (ver servicio de Google Ads)",
          "Implementación técnica de SEO (ver SEO Técnico)",
          "Gestión de redes sociales pagadas u otros canales de medios",
          "Garantías de reducción de gasto o de resultados",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Negocios que ya invierten en SEO y SEM por separado y sospechan que se solapan",
          "Equipos con proveedores distintos para cada canal que necesitan un criterio común",
          "Empresas que deben decidir cómo repartir un presupuesto limitado de búsqueda",
          "Negocios en expansión que necesitan definir la secuencia entre visibilidad inmediata y sostenida",
        ],
      },
      notFor: {
        title: "Para quién no es el mejor ajuste",
        items: [
          "Quien busca solo la ejecución táctica de un canal (ver Google Ads o SEO Técnico por separado)",
          "Negocios sin ningún tipo de inversión actual en búsqueda para comparar",
          "Quien espera una fórmula fija de reparto de presupuesto sin analizar su caso",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿En qué se diferencia esto de contratar SEO y Google Ads por separado?",
          answer:
            "La diferencia está en la capa de decisión previa: antes de ejecutar cualquiera de los dos canales, se define qué papel cumple cada uno, dónde se solapan y en qué orden conviene invertir. Sin esa capa, cada canal se optimiza de forma aislada, a veces en contra del otro.",
        },
        {
          question: "¿Esto reemplaza la gestión operativa de Google Ads o el trabajo técnico de SEO?",
          answer:
            "No. Es la estrategia que informa esa ejecución. La gestión diaria de campañas y el trabajo técnico de SEO son servicios distintos que se coordinan con esta estrategia, no la reemplazan.",
        },
        {
          question: "¿Cómo se decide qué palabras clave van a SEO y cuáles a SEM?",
          answer:
            "Se evalúa la dificultad de posicionamiento orgánico, el margen del producto o servicio, la urgencia del negocio y la intención real detrás de la búsqueda. No hay una regla universal: depende del contexto de cada negocio.",
        },
        {
          question: "¿Puedo aplicar esta estrategia si ya tengo agencias distintas para SEO y para Ads?",
          answer:
            "Sí. De hecho es uno de los escenarios más comunes: la estrategia sirve como marco de coordinación entre proveedores existentes, sin necesidad de reemplazarlos.",
        },
        {
          question: "¿Cuánto se puede ahorrar eliminando el solapamiento entre canales?",
          answer:
            "No doy una cifra porque depende de cuánto solapamiento exista realmente y del volumen de inversión actual. El objetivo no es solo ahorrar, sino asegurar que cada dólar de búsqueda esté haciendo un trabajo distinto.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Esta estrategia se apoya en la ejecución de ambos canales",
      links: [
        {
          label: "SEO Técnico",
          description: "La base orgánica sobre la que se apoya cualquier decisión de asignación de canal.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Auditoría SEO profesional",
          description: "Diagnóstico del estado orgánico actual antes de definir la estrategia conjunta.",
          to: "/servicios/seo-diagnostico",
        },
        {
          label: "Consultoría SEO",
          description: "Acompañamiento estratégico continuo más allá de la asignación inicial de canal.",
          to: "/consultor-seo",
        },
      ],
    },
    llmSummary:
      "Estrategia integrada de SEO y SEM que decide cómo asignar palabras clave e inversión entre búsqueda orgánica y pagada, secuenciar el trabajo y medir el resultado conjunto sin duplicar gasto.",
    keyPoints: [
      "Define qué canal prioriza cada palabra clave o segmento antes de ejecutar",
      "Establece la secuencia de inversión entre SEO y SEM según tiempo y presupuesto",
      "Propone un marco de medición compartido entre ambos canales",
      "No reemplaza la ejecución operativa de SEO técnico ni de Google Ads",
    ],
  },
  en: {
    metaTitle: "SEO & SEM Strategy | Search Channel Allocation",
    metaDescription:
      "Integrated SEO and SEM strategy: which keywords to prioritize per channel, how to sequence investment, and how to measure without duplicating spend.",
    keywords: [
      "seo and sem strategy",
      "integrated seo sem",
      "search budget allocation",
      "keyword overlap",
      "seo sem measurement",
      "search strategy",
    ],
    h1: "Integrated SEO and SEM strategy to decide where each channel earns its budget",
    breadcrumbLabel: "SEO & SEM Strategy",
    heroSubtitle: "One search strategy, two channels",
    heroDescription:
      "SEO and SEM usually get managed separately, by different teams or vendors who never compare notes. The result is paid budget competing with organic rankings that already work, or the opposite: relying on a slow channel when the business needs results now. I work on channel allocation, investment sequencing and shared measurement across both.",
    ctaPrimary: {
      label: "Request a Search Strategy Review",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See how I work", to: "/en/case-studies" },
    summary: [
      {
        label: "Channel allocation",
        text: "Which keywords and intents are better pursued organically, which with paid, and which with both.",
      },
      {
        label: "Sequencing",
        text: "In what order to invest when time, budget and competition don't allow doing everything at once.",
      },
      {
        label: "Shared measurement",
        text: "A measurement framework that avoids crediting the same result twice to different channels.",
      },
      {
        label: "Removing duplicate spend",
        text: "Finding where you're paying for something SEO is already delivering for free.",
      },
    ],
    explanation: {
      title: "What an integrated SEO/SEM strategy is (and isn't)",
      definition:
        "It's the decision layer that sits above execution in each channel: it defines what role SEO plays, what role SEM plays, and how they complement each other given the business goal, available time and budget. It's not running both channels in parallel without coordination, and it's not a technical audit of either one.",
      whyImportant: {
        title: "Why allocation matters more than isolated execution",
        text: "Optimizing SEO and SEM separately, without a joint decision on where each keyword should live, leads to competing with yourself: paying for clicks on terms you already rank for organically, or waiting months for organic traction when the business needed visibility now.",
      },
      consequences: {
        title: "What happens without a joint strategy",
        text: "Paid budget spent on keywords the site already ranks for, long-term organic opportunities ignored because the focus stayed on immediate Ads results, and per-channel reports that never show the full picture of search investment.",
      },
    },
    problems: {
      title: "Common mistakes when SEO and SEM are managed separately",
      subtitle: "Most come from a missing joint decision, not from bad execution in either channel alone",
      items: [
        {
          icon: GitMerge,
          title: "Keyword overlap with no decision behind it",
          description:
            "Paying for ads on terms where the site already holds a strong organic position, without evaluating whether that adds incremental value.",
          impact: "Paid budget occupying a spot SEO was already covering for free.",
        },
        {
          icon: Layers,
          title: "Teams or vendors that don't talk to each other",
          description:
            "SEO and SEM run by different people or agencies, each optimizing their own channel with no visibility into the other.",
          impact: "Contradictory decisions about which pages or terms to prioritize.",
        },
        {
          icon: AlertTriangle,
          title: "Wrong sequencing of investment",
          description:
            "Putting the entire budget into SEM without building an organic base, or waiting on organic results when the business needed visibility in weeks, not months.",
          impact: "Budget poorly matched to the business's actual time horizon.",
        },
        {
          icon: BarChart3,
          title: "Fragmented per-channel measurement",
          description:
            "Each channel reporting its own numbers with no shared framework showing the combined effect on the business.",
          impact: "Budget decisions made with only a partial view of the real outcome.",
        },
        {
          icon: Target,
          title: "No criteria for deciding where to compete",
          description:
            "Chasing the same high-volume keywords in both channels without considering difficulty, margin or actual search intent.",
          impact: "Effort concentrated on terms that aren't the ones converting best for the business.",
        },
      ],
    },
    methodology: {
      title: "How I approach SEO and SEM strategy",
      subtitle: "Channel decision before tactical execution",
      steps: [
        {
          number: "01",
          title: "Intent and overlap mapping",
          description:
            "Identifying which keywords and intents are already covered organically, which require paid support, and where both channels compete against each other.",
        },
        {
          number: "02",
          title: "Channel allocation by objective",
          description:
            "Defining what should be solved with SEO given its long-term nature, and what should be solved with SEM given its ability to deliver immediate results.",
        },
        {
          number: "03",
          title: "Investment sequencing",
          description:
            "Setting the order and relative weight of investment in each channel based on time horizon and available budget.",
        },
        {
          number: "04",
          title: "Shared measurement framework",
          description:
            "Defining how the combined effect of both channels gets reported, to avoid duplicate or contradictory attribution.",
        },
      ],
      ctaText: "Request a Search Strategy Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "Overlap and intent mapping between SEO and SEM",
          "Definition of which channel should be prioritized per keyword or segment",
          "Investment sequencing plan across both channels",
          "Shared measurement framework for SEO and SEM",
          "Recommendations for coordinating existing teams or vendors",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Day-to-day operational management of Google Ads campaigns (see Google Ads service)",
          "Technical SEO implementation (see Technical SEO)",
          "Management of paid social or other media channels",
          "Guarantees of reduced spend or specific results",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this makes sense for",
        items: [
          "Businesses already investing in SEO and SEM separately and suspecting overlap",
          "Teams with different vendors per channel who need a shared framework",
          "Companies that need to decide how to split a limited search budget",
          "Growing businesses that need to sequence immediate visibility against sustained organic growth",
        ],
      },
      notFor: {
        title: "Who this isn't the best fit for",
        items: [
          "Anyone looking only for tactical execution of a single channel (see Google Ads or Technical SEO separately)",
          "Businesses with no current search investment to compare against",
          "Anyone expecting a fixed budget-split formula without analyzing their own case",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "How is this different from hiring SEO and Google Ads separately?",
          answer:
            "The difference is the decision layer that comes first: before either channel is executed, this defines the role each one plays, where they overlap, and in what order investment should happen. Without that layer, each channel gets optimized in isolation, sometimes against the other.",
        },
        {
          question: "Does this replace day-to-day Google Ads management or technical SEO work?",
          answer:
            "No. It's the strategy that informs that execution. Day-to-day campaign management and technical SEO work are separate services that get coordinated with this strategy, not replaced by it.",
        },
        {
          question: "How do you decide which keywords go to SEO versus SEM?",
          answer:
            "By evaluating organic ranking difficulty, product or service margin, business urgency and the real intent behind the search. There's no universal rule — it depends on each business's context.",
        },
        {
          question: "Can I use this if I already have separate agencies for SEO and Ads?",
          answer:
            "Yes. That's actually one of the most common scenarios: the strategy serves as a coordination framework across existing vendors, without needing to replace them.",
        },
        {
          question: "How much can I save by removing overlap between channels?",
          answer:
            "I don't quote a number, since it depends on how much real overlap exists and your current investment volume. The goal isn't just savings — it's making sure every search dollar is doing distinct work.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "This strategy relies on execution in both channels",
      links: [
        {
          label: "Technical SEO",
          description: "The organic foundation that any channel-allocation decision depends on.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Professional SEO Audit",
          description: "A read on current organic standing before defining the joint strategy.",
          to: "/en/services/seo-audit",
        },
        {
          label: "SEO Consulting",
          description: "Ongoing strategic guidance beyond the initial channel allocation.",
          to: "/en/seo-consultant",
        },
      ],
    },
    llmSummary:
      "Integrated SEO and SEM strategy that decides how to allocate keywords and investment between organic and paid search, sequence the work, and measure combined results without duplicating spend.",
    keyPoints: [
      "Defines which channel should own each keyword or segment before execution",
      "Sets the investment sequence between SEO and SEM based on time and budget",
      "Proposes a shared measurement framework across both channels",
      "Does not replace the operational execution of technical SEO or Google Ads",
    ],
  },
};
